import { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  MoreHorizontal,
  Star,
  Edit3,
  Trash2,
  Folder,
  FolderInput,
  MessageSquare,
  UsersRound,
  Share2,
  Crown,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Conversation, Project } from '@/types';
import { useBoxSelection } from '@/hooks/ui/useBoxSelection';
import { useTranslation } from 'react-i18next';
import i18nInstance from '@/i18n/config';

// 🔥 格式化为精确到秒的绝对时间，如 Apr 2, 2026, 11:20:22
const formatAbsoluteDate = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return dateStr;
  }
};

// Hook to force re-render on language change
const useForceUpdate = () => {
  const [, setForceUpdate] = useState(0);
  useEffect(() => {
    const onLanguageChanged = () => setForceUpdate(n => n + 1);
    i18nInstance.on('languageChanged', onLanguageChanged);
    return () => {
      i18nInstance.off('languageChanged', onLanguageChanged);
    };
  }, []);
};

interface ConversationListPanelProps {
  conversations: Conversation[];
  isLoading: boolean;
  searchQuery?: string;
  selectedIds: Set<string>;
  isViewer?: boolean; // 🔥 是否为 viewer 角色（只读模式）- 全局设置
  projectArchived?: boolean; // 🔥 项目是否已归档，用于禁用 Star/Share
  projects?: Project[]; // 🔥 项目列表，用于显示项目信息和角色
  onConversationSelect: (conversationId: string) => void;
  onToggleSelect: (conversationId: string, event: React.MouseEvent) => void;
  onMenuAction: (
    action: string,
    conversationId: string,
    event: React.MouseEvent
  ) => void;
  onShowMore?: () => void;
  hasMore?: boolean;
  showMoreLabel?: string;
  showProjectMenu?: boolean; // true for ChatsPage, false for ProjectDetail
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  containerClassName?: string;
  formatDate: (date: string) => string;
  onBatchSelect?: (ids: string[]) => void; // Callback for box selection
  disableSelection?: boolean; // Disable selection and batch operations (for group chats)
  currentUserEmail?: string; // Current user email to identify owner in group chats
}

export default function ConversationListPanel({
  conversations,
  isLoading,
  searchQuery = '',
  selectedIds,
  isViewer = false,
  projectArchived = false,
  projects = [],
  onConversationSelect,
  onToggleSelect,
  onMenuAction,
  onShowMore,
  hasMore = false,
  showMoreLabel,
  showProjectMenu = true,
  scrollContainerRef,
  containerClassName,
  formatDate,
  onBatchSelect,
  disableSelection = false,
  // currentUserEmail,
}: ConversationListPanelProps) {
  const { t } = useTranslation();
  useForceUpdate();

  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
    openUpward?: boolean;
  } | null>(null);
  const buttonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const itemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const containerRef = useRef<HTMLDivElement>(null);

  // 🔥 计算 viewer 角色的对话 ID 集合（批量选择时需要过滤）
  const viewerConversationIds = useMemo(() => {
    const viewerIds = new Set<string>();
    conversations.forEach(conversation => {
      const conversationProject = conversation.projectId
        ? projects.find(p => p.id === conversation.projectId)
        : null;
      const projectRole = conversationProject?.role || null;
      const isConversationViewer =
        projectRole === 'viewer' || (!projectRole && isViewer);
      if (isConversationViewer) {
        viewerIds.add(conversation.conversationId);
      }
    });
    return viewerIds;
  }, [conversations, projects, isViewer]);

  // 🔥 过滤后的批量选择处理器（排除 viewer 对话）
  const filteredBatchSelect = useCallback(
    (ids: string[]) => {
      if (!onBatchSelect) return;
      // 过滤掉 viewer 角色的对话
      const filteredIds = ids.filter(id => !viewerConversationIds.has(id));
      onBatchSelect(filteredIds);
    },
    [onBatchSelect, viewerConversationIds]
  );

  // Use box selection hook
  const { isSelecting, selectionBoxStyle, handleMouseDown } = useBoxSelection({
    containerRef,
    itemRefs,
    onSelect: filteredBatchSelect,
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (menuOpenId) {
        setMenuOpenId(null);
        setMenuPosition(null);
      }
    };

    if (menuOpenId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [menuOpenId]);

  // Conversations List
  return (
    <div className={cn('flex-1 min-h-0 flex flex-col', containerClassName)}>
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400"></div>
          <span className="ml-3 text-gray-500">
            {t('sidebar.loadingConversations')}
          </span>
        </div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-12">
          <div className="mb-4">
            <MessageSquare size={48} className="text-gray-300 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-600 mb-2">
            {searchQuery
              ? t('sidebar.noConversations')
              : t('sidebar.noConversations')}
          </h3>
          <p className="text-gray-500 text-sm">
            {searchQuery ? t('project.tryDifferentSearch') : t('chat.newChat')}
          </p>
        </div>
      ) : (
        <>
          <div
            ref={el => {
              (
                containerRef as React.MutableRefObject<HTMLDivElement | null>
              ).current = el;
              if (scrollContainerRef) {
                (
                  scrollContainerRef as React.MutableRefObject<HTMLDivElement | null>
                ).current = el;
              }
            }}
            className="flex-1 mb-10 min-h-0 overflow-y-auto relative select-none"
            onMouseDown={disableSelection ? undefined : handleMouseDown}
          >
            {/* Selection box overlay - rendered via portal for proper positioning during scroll */}
            {isSelecting &&
              !disableSelection &&
              createPortal(
                <div
                  className="border-2 border-blue-500 bg-blue-500/10"
                  style={selectionBoxStyle}
                />,
                document.body
              )}
            <div>
              {conversations.map((conversation, index) => {
                const isSelected = selectedIds.has(conversation.conversationId);
                const isHovered = hoveredId === conversation.conversationId;

                // Check if previous and next items are selected
                const prevConversation = conversations[index - 1];
                const nextConversation = conversations[index + 1];
                const isPrevSelected =
                  prevConversation &&
                  selectedIds.has(prevConversation.conversationId);
                const isNextSelected =
                  nextConversation &&
                  selectedIds.has(nextConversation.conversationId);

                // Determine if this is the first, last, or middle of a selection group
                const isFirstInGroup = isSelected && !isPrevSelected;
                const isLastInGroup = isSelected && !isNextSelected;

                // Determine if need to hide bottom border
                // const prevIsLastInGroup = !isSelected && isNextSelected;
                // const shouldHideBorder = isLastInGroup || prevIsLastInGroup;

                // Build border radius classes
                let roundedClass = '';
                if (isSelected) {
                  if (isFirstInGroup && isLastInGroup) {
                    roundedClass = 'rounded-lg'; // Single selected item
                  } else if (isFirstInGroup) {
                    roundedClass = 'rounded-t-lg'; // First in group
                  } else if (isLastInGroup) {
                    roundedClass = 'rounded-b-lg'; // Last in group
                  }
                  // Middle items have no border radius
                }

                // 🔥 查找对话所属的项目并获取角色
                const conversationProject = conversation.projectId
                  ? projects.find(p => p.id === conversation.projectId)
                  : null;
                const projectRole = conversationProject?.role || null;
                // 基于项目角色判断是否为 viewer（优先使用项目角色，否则使用全局 isViewer）
                const isConversationViewer =
                  projectRole === 'viewer' || (!projectRole && isViewer);

                return (
                  <div
                    key={conversation.conversationId}
                    ref={el => {
                      if (el) {
                        itemRefs.current.set(conversation.conversationId, el);
                      } else {
                        itemRefs.current.delete(conversation.conversationId);
                      }
                    }}
                    onMouseEnter={() =>
                      setHoveredId(conversation.conversationId)
                    }
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => {
                      if (!isSelecting) {
                        onConversationSelect(conversation.conversationId);
                      }
                    }}
                    className="group relative transition-all cursor-pointer flex items-center"
                  >
                    {/* Checkbox - show on hover or when selected, hide when disableSelection is true */}
                    {/* 🔥 修复：loading 状态或 viewer 角色时禁用复选框（而不是隐藏，避免 UI 不对齐） */}
                    {!disableSelection && (
                      <div className="flex-shrink-0 mr-2">
                        <div
                          className={`transition-opacity ${
                            isHovered || selectedIds.size > 0
                              ? 'opacity-100'
                              : 'opacity-0'
                          }`}
                          onClick={e => {
                            e.stopPropagation();
                            // 🔥 loading 状态或 viewer 角色时禁止选择
                            if (conversation.isLoading || isConversationViewer)
                              return;
                            onToggleSelect(conversation.conversationId, e);
                          }}
                        >
                          <div
                            className={`w-4 h-4 border rounded flex items-center justify-center ${
                              conversation.isLoading || isConversationViewer
                                ? 'cursor-not-allowed opacity-50'
                                : 'cursor-pointer'
                            } ${
                              isSelected
                                ? 'bg-[#2c84db] border-[#2c84db]'
                                : conversation.isLoading || isConversationViewer
                                  ? 'border-gray-300 bg-gray-100'
                                  : 'border-gray-400 hover:border-gray-600 bg-white'
                            }`}
                          >
                            {isSelected && (
                              <svg
                                className="w-2.5 h-2.5 text-white"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2.5"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Content */}
                    <div
                      className={`flex items-center flex-1 h-full min-w-0 border-b border-[#1f1e1d26] ${
                        isSelected
                          ? `bg-[#d3e5f866] ${roundedClass} hover:bg-[#d3e5f892]`
                          : 'hover:bg-[#f5f4ed]'
                      }`}
                    >
                      <div
                        className={`flex flex-col flex-1 min-w-0 px-3 py-3 transition-all w-0 ${conversation.conversationType === 'group' && (showProjectMenu || disableSelection) ? 'ml-5' : ''}`}
                      >
                        <div className="flex items-start w-full mb-1 gap-2 min-w-0">
                          {/* 🔥 群聊未访问红点指示器 */}
                          {conversation.conversationType === 'group' &&
                            (conversation.lastVisitedAt === null ||
                              conversation.lastVisitedAt === undefined) && (
                              <div className="flex-shrink-0 w-2 h-2 bg-red-500 rounded-full" />
                            )}
                          {conversation.conversationType === 'group' && (
                            <UsersRound size={14} className="text-gray-600" />
                          )}
                          {/* 🔥 群聊 owner 皇冠标记 - 直接判断当前用户的 role */}
                          {conversation.conversationType === 'group' &&
                            (conversation as any).role === 'owner' && (
                              <Crown
                                size={14}
                                className="text-yellow-500 flex-shrink-0"
                              />
                            )}
                          <h3
                            className="font-normal text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis min-w-0 shrink"
                            title={conversation.title}
                          >
                            {conversation.title}
                          </h3>
                          {/* 🔥 项目信息和角色标签 - 紧贴名称后面，有边框，采用 [role]+name 格式 */}
                          {conversationProject && (
                            <span
                              className="flex-shrink-0 flex items-center px-1 sm:px-2 py-0.5 text-xs border rounded-sm transition-colors text-gray-600 bg-blue-50 hover:bg-blue-100 border-blue-200 overflow-hidden"
                              style={{ maxWidth: '10rem' }}
                              title={`${projectRole || ''}${projectRole ? ' | ' : ''}${conversationProject.name}`}
                            >
                              <span
                                className={`flex-shrink-0 pr-1 font-bold ${
                                  projectRole === 'owner'
                                    ? 'text-purple-700'
                                    : projectRole === 'manager'
                                      ? 'text-blue-700'
                                      : projectRole === 'editor'
                                        ? 'text-green-700'
                                        : 'text-gray-600'
                                }`}
                              >
                                {projectRole
                                  ? '[' + t(`roles.${projectRole}`) + ']'
                                  : ''}
                              </span>
                              <span className="truncate">
                                {conversationProject.name}
                              </span>
                            </span>
                          )}
                          {conversation.isLoading && (
                            <div className="flex-shrink-0 ml-auto">
                              <div className="animate-spin rounded-full h-3 w-3 border-b border-gray-400"></div>
                            </div>
                          )}
                        </div>
                        <div className="flex justify-between gap-2 pt-1">
                          <span className="text-xs text-gray-500 truncate">
                            {t('chat.lastMessage')}{' '}
                            {formatDate(conversation.updatedAt)}
                          </span>
                          <span className="text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                            {t('chat.createdAt')}{' '}
                            {formatAbsoluteDate(conversation.createdAt)}
                          </span>
                        </div>
                      </div>

                      {/* Three dots menu - show on hover, but hide when another menu is open */}
                      {/* 🔥 修复：始终渲染按钮容器（用 CSS 控制可见性），避免 hover 时布局抖动 */}
                      <div
                        className={`relative flex-shrink-0 transition-opacity ${
                          (isHovered ||
                            isSelected ||
                            menuOpenId === conversation.conversationId) &&
                          selectedIds.size === 0 &&
                          (!menuOpenId ||
                            menuOpenId === conversation.conversationId) &&
                          !conversation.isLoading
                            ? 'opacity-100'
                            : 'opacity-0 pointer-events-none'
                        }`}
                      >
                        <button
                          ref={el => {
                            if (el) {
                              buttonRefs.current.set(
                                conversation.conversationId,
                                el
                              );
                            } else {
                              buttonRefs.current.delete(
                                conversation.conversationId
                              );
                            }
                          }}
                          onClick={e => {
                            e.stopPropagation();
                            const isOpening =
                              menuOpenId !== conversation.conversationId;
                            setMenuOpenId(
                              isOpening ? conversation.conversationId : null
                            );

                            if (isOpening) {
                              const button = buttonRefs.current.get(
                                conversation.conversationId
                              );
                              if (button) {
                                const rect = button.getBoundingClientRect();
                                // 计算菜单需要的高度（根据是否有项目ID决定）
                                const menuHeight = conversation.projectId
                                  ? 320
                                  : 280;
                                // 判断下方空间是否足够
                                const spaceBelow =
                                  window.innerHeight - rect.bottom;
                                const shouldOpenUpward =
                                  spaceBelow < menuHeight;

                                setMenuPosition({
                                  top: shouldOpenUpward
                                    ? rect.top - 8
                                    : rect.bottom + 8,
                                  right: window.innerWidth - rect.right,
                                  openUpward: shouldOpenUpward,
                                });
                              }
                            }
                          }}
                          className="p-1.5 hover:bg-[#f5f4ed] rounded transition-colors"
                        >
                          <MoreHorizontal size={18} className="text-gray-600" />
                        </button>

                        {/* Dropdown Menu via Portal */}
                        {menuOpenId === conversation.conversationId &&
                          menuPosition &&
                          createPortal(
                            <div
                              className={`fixed bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999] ${conversation.projectId ? 'w-56' : 'w-48'}`}
                              style={{
                                top: menuPosition.openUpward
                                  ? 'auto'
                                  : `${menuPosition.top}px`,
                                bottom: menuPosition.openUpward
                                  ? `${window.innerHeight - menuPosition.top}px`
                                  : 'auto',
                                right: `${menuPosition.right}px`,
                              }}
                              onClick={e => e.stopPropagation()}
                            >
                              {/* 🔥 viewer 角色禁用 Select */}
                              <button
                                onClick={e => {
                                  if (isConversationViewer) return;
                                  e.stopPropagation();
                                  onMenuAction(
                                    'select',
                                    conversation.conversationId,
                                    e
                                  );
                                  setMenuOpenId(null);
                                }}
                                disabled={isConversationViewer}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ${
                                  isConversationViewer
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <div className="w-4 h-4 border-2 border-gray-400 rounded flex items-center justify-center">
                                  {isSelected && (
                                    <svg
                                      className="w-2.5 h-2.5 text-blue-600"
                                      fill="currentColor"
                                      viewBox="0 0 16 16"
                                    >
                                      <path d="M13.854 3.646a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708 0l-3.5-3.5a.5.5 0 1 1 .708-.708L6.5 10.293l6.646-6.647a.5.5 0 0 1 .708 0z" />
                                    </svg>
                                  )}
                                </div>
                                <span>{t('chatsPage.select')}</span>
                              </button>

                              {/* Star 功能不受 viewer 角色限制，但 archived 项目禁用 */}
                              <button
                                onClick={e => {
                                  if (projectArchived) return;
                                  e.stopPropagation();
                                  onMenuAction(
                                    'star',
                                    conversation.conversationId,
                                    e
                                  );
                                  setMenuOpenId(null);
                                }}
                                disabled={projectArchived}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ${
                                  projectArchived
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                {conversation.starred ? (
                                  <Star
                                    size={16}
                                    className="text-yellow-500"
                                    fill="currentColor"
                                  />
                                ) : (
                                  <Star size={16} className="text-yellow-500" />
                                )}
                                <span>
                                  {conversation.starred
                                    ? t('chat.unstar')
                                    : t('chat.star')}
                                </span>
                              </button>

                              {/* 🔥 viewer 角色禁用 Rename */}
                              <button
                                onClick={e => {
                                  if (isConversationViewer) return;
                                  e.stopPropagation();
                                  onMenuAction(
                                    'rename',
                                    conversation.conversationId,
                                    e
                                  );
                                  setMenuOpenId(null);
                                }}
                                disabled={isConversationViewer}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ${
                                  isConversationViewer
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <Edit3 size={16} />
                                <span>{t('tooltips.rename')}</span>
                              </button>

                              {conversation.projectId && showProjectMenu ? (
                                <>
                                  {/* 🔥 viewer 角色禁用 Change project */}
                                  <button
                                    onClick={e => {
                                      if (isConversationViewer) return;
                                      e.stopPropagation();
                                      onMenuAction(
                                        'changeProject',
                                        conversation.conversationId,
                                        e
                                      );
                                      setMenuOpenId(null);
                                    }}
                                    disabled={isConversationViewer}
                                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ${
                                      isConversationViewer
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                  >
                                    <FolderInput size={16} />
                                    <span>{t('chat.changeProject')}</span>
                                  </button>
                                  {/* 🔥 viewer 角色禁用 Remove from project */}
                                  <button
                                    onClick={e => {
                                      if (isConversationViewer) return;
                                      e.stopPropagation();
                                      onMenuAction(
                                        'removeFromProject',
                                        conversation.conversationId,
                                        e
                                      );
                                      setMenuOpenId(null);
                                    }}
                                    disabled={isConversationViewer}
                                    className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ${
                                      isConversationViewer
                                        ? 'text-gray-400 cursor-not-allowed'
                                        : 'text-gray-700 hover:bg-gray-50'
                                    }`}
                                  >
                                    <div
                                      className="flex items-center justify-center"
                                      style={{ width: 20, height: 20 }}
                                    >
                                      <svg
                                        width="20"
                                        height="20"
                                        viewBox="0 0 20 20"
                                        fill="currentColor"
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="shrink-0"
                                        aria-hidden="true"
                                      >
                                        <path d="M17.1464 12.1465C17.3416 11.9514 17.6582 11.9514 17.8534 12.1465C18.0486 12.3417 18.0486 12.6582 17.8534 12.8535L15.7069 15L17.8534 17.1465C18.0486 17.3417 18.0486 17.6582 17.8534 17.8535C17.6581 18.0487 17.3416 18.0488 17.1464 17.8535L14.9999 15.707L12.8534 17.8535C12.6581 18.0487 12.3416 18.0488 12.1464 17.8535C11.9513 17.6583 11.9512 17.3417 12.1464 17.1465L14.2928 15L12.1464 12.8535C11.9513 12.6583 11.9512 12.3417 12.1464 12.1465C12.3416 11.9514 12.6581 11.9514 12.8534 12.1465L14.9999 14.293L17.1464 12.1465ZM15.8192 7C16.7461 7 17.4511 7.83277 17.2987 8.74707L17.243 9.08203C17.1976 9.35435 16.9402 9.53846 16.6678 9.49316C16.3956 9.44773 16.2114 9.19025 16.2567 8.91797L16.3124 8.58203C16.3631 8.27731 16.1281 8 15.8192 8H4.18054C3.87167 8.00007 3.63669 8.27736 3.68738 8.58203L4.85339 15.582C4.89355 15.8229 5.10238 15.9997 5.34656 16H8.99988C9.27602 16 9.49988 16.2239 9.49988 16.5C9.49988 16.7761 9.27602 17 8.99988 17H5.34656C4.61367 16.9997 3.98775 16.47 3.86707 15.7471L2.70105 8.74707C2.54868 7.83282 3.25369 7.00007 4.18054 7H15.8192ZM15.5009 5C15.7766 5.00044 16.0009 5.22413 16.0009 5.5C16.0009 5.77587 15.7766 5.99956 15.5009 6H4.50085C4.22471 6 4.00085 5.77614 4.00085 5.5C4.00085 5.22386 4.22471 5 4.50085 5H15.5009ZM14.0009 3C14.2766 3.00044 14.5009 3.22413 14.5009 3.5C14.5009 3.77587 14.2766 3.99956 14.0009 4H6.00085C5.72471 4 5.50085 3.77614 5.50085 3.5C5.50085 3.22386 5.72471 3 6.00085 3H14.0009Z"></path>
                                      </svg>
                                    </div>
                                    <span>{t('chat.removeFromProject')}</span>
                                  </button>
                                </>
                              ) : !conversation.projectId && showProjectMenu ? (
                                /* 🔥 viewer 角色禁用 Add to project */
                                <button
                                  onClick={e => {
                                    if (isConversationViewer) return;
                                    e.stopPropagation();
                                    onMenuAction(
                                      'addToProject',
                                      conversation.conversationId,
                                      e
                                    );
                                    setMenuOpenId(null);
                                  }}
                                  disabled={isConversationViewer}
                                  className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ${
                                    isConversationViewer
                                      ? 'text-gray-400 cursor-not-allowed'
                                      : 'text-gray-700 hover:bg-gray-50'
                                  }`}
                                >
                                  <Folder size={16} />
                                  <span>{t('chat.addToProject')}</span>
                                </button>
                              ) : null}

                              {/* 🔥 archived 项目禁用 Share（viewer 角色可以分享） */}
                              <button
                                onClick={e => {
                                  if (projectArchived) return;
                                  e.stopPropagation();
                                  onMenuAction(
                                    'share',
                                    conversation.conversationId,
                                    e
                                  );
                                  setMenuOpenId(null);
                                }}
                                disabled={projectArchived}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ${
                                  projectArchived
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-gray-700 hover:bg-gray-50'
                                }`}
                              >
                                <Share2 size={16} />
                                <span>{t('tooltips.share')}</span>
                              </button>
                              <div className="border-t border-gray-200 my-1"></div>

                              {/* 🔥 viewer 角色禁用 Delete */}
                              <button
                                onClick={e => {
                                  if (isConversationViewer) return;
                                  e.stopPropagation();
                                  onMenuAction(
                                    'delete',
                                    conversation.conversationId,
                                    e
                                  );
                                  setMenuOpenId(null);
                                }}
                                disabled={isConversationViewer}
                                className={`w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors text-left ${
                                  isConversationViewer
                                    ? 'text-gray-400 cursor-not-allowed'
                                    : 'text-red-600 hover:bg-red-50'
                                }`}
                              >
                                <Trash2 size={16} />
                                <span>{t('common.delete')}</span>
                              </button>
                            </div>,
                            document.body
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {/* Show More Button (inside scroll area to avoid clipping) */}
            {hasMore && !isLoading && onShowMore && (
              <div className="px-2 pt-3 pb-2">
                <button
                  onClick={onShowMore}
                  className="w-full py-3 text-center text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 transition-colors rounded-lg border border-gray-200"
                >
                  {showMoreLabel || t('chat.showMore')}
                </button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
